import { createHttpClient } from '@adapters/HttpClient';
import { PackingStationsApiAdapter } from '@modules/Packaging/PackingStations/List/adapters/PackingStationsApiAdapter';
import { PackingStationDetail } from '@modules/Packaging/PackingStations/List/models/PackingStation';
import { apiClientWH } from '@shared/services';
import { normalizePackingScanCode } from '../utils/packingAssignKey';
import { useCallback, useMemo, useState } from 'react';
import { Order, PackagingDetail } from '../models';
import { ordersRequirePackingStation, PackingStationLocationEntry } from '../utils/packingStationPackaging';

export interface PackingStationSelection {
  id: number;
  code: string;
  locations: PackingStationLocationEntry[];
}

const mapStationDetail = (detail: PackingStationDetail): PackingStationSelection => ({
  id: detail.id,
  code: detail.code,
  locations: (detail.locations || []) as PackingStationLocationEntry[],
});

export const usePackingStationScan = (orders: Order[] = [], packagings: PackagingDetail[] = []) => {
  const [packingStation, setPackingStation] = useState<PackingStationSelection | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const needsPackingStationScan = useMemo(() => ordersRequirePackingStation(orders, packagings), [orders, packagings]);
  const isPackingStationReady = !needsPackingStationScan || Boolean(packingStation);

  const scanPackingStation = useCallback(async (code: string): Promise<{ success: boolean; message: string }> => {
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      return { success: false, message: 'Código de puesto vacío' };
    }

    setIsLoading(true);
    try {
      const adapter = new PackingStationsApiAdapter(createHttpClient(apiClientWH));
      const page = await adapter.getPackingStationsPaginated({
        active: true,
        code: trimmedCode,
        limit: 20,
      });

      const normalized = normalizePackingScanCode(trimmedCode);
      const match = page.packingStations.find((s) => normalizePackingScanCode(s.code) === normalized);

      if (!match) {
        return {
          success: false,
          message: `Puesto "${trimmedCode}" no encontrado. Escanea un código de puesto activo.`,
        };
      }

      const detail = await adapter.getPackingStationById(match.id);
      if (!detail.active) {
        return { success: false, message: `El puesto "${detail.code}" está inactivo.` };
      }

      setPackingStation(mapStationDetail(detail));
      return { success: true, message: `Puesto de trabajo confirmado (${detail.code})` };
    } catch {
      return { success: false, message: 'Error al cargar el puesto de trabajo. Inténtalo de nuevo.' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    needsPackingStationScan,
    isPackingStationReady,
    packingStation,
    scanPackingStation,
    isPackingStationLoading: isLoading,
  };
};
